import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Check, Clock, Euro, Shield, Smartphone, ChevronRight, Play, ArrowRight, Menu, X,
  MessageCircleQuestion, Users, TrendingUp, Calculator, AlertTriangle, Zap, 
  FileCheck, ThermometerSun, Timer, HeartHandshake, Award, Building2
} from "lucide-react";
import SignupModal from "@/components/SignupModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/packages`);
      const data = await response.json();
      setPackages(data.packages);
    } catch (error) {
      console.error("Error fetching packages:", error);
      setPackages([
        {
          id: "starter",
          name: "Starter",
          price: 129,
          period: "per maand",
          features: ["Tijdslijnen voor alle vloertypen", "Basis checklist voor klanten", "Digitale verklaring"],
          popular: false
        },
        {
          id: "professional",
          name: "Professional",
          price: 179,
          period: "per maand",
          features: ["Alles van Starter", "Vloerverwarming protocollen", "Extra richtlijnen", "Prioriteit support"],
          popular: true
        },
        {
          id: "whitelabel",
          name: "Premium White-label",
          price: 225,
          period: "per maand",
          setup_fee: 1950,
          features: ["Volledig in uw eigen huisstijl", "Eigen afzender & klantomgeving", "Inclusief setup & branding", "Dedicated support"],
          popular: false
        }
      ]);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setIsModalOpen(true);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-[#C9A227]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 border-2 border-[#C9A227] rounded-lg flex items-center justify-center">
                <span className="text-[#C9A227] font-bold text-sm">NL</span>
              </div>
              <span className="font-semibold text-gray-900">Next Level Protocol</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('waarom')} className="text-gray-600 hover:text-[#C9A227] text-sm font-medium transition-colors">Waarom</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-[#C9A227] text-sm font-medium transition-colors">Voordelen</button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-600 hover:text-[#C9A227] text-sm font-medium transition-colors">Prijzen</button>
              <button onClick={() => scrollToSection('demo')} className="text-gray-600 hover:text-[#C9A227] text-sm font-medium transition-colors">Demo</button>
              <Link to="/over-ons" className="text-gray-600 hover:text-[#C9A227] text-sm font-medium transition-colors">Over Ons</Link>
              <Button 
                onClick={() => scrollToSection('pricing')} 
                className="border-2 border-[#C9A227] bg-transparent text-[#C9A227] hover:bg-[#C9A227] hover:text-white font-semibold"
                data-testid="nav-start-btn"
              >
                Start Nu
              </Button>
            </div>

            <button 
              className="md:hidden p-2 text-gray-900" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#C9A227]/20 py-4 px-4 space-y-3">
            <button onClick={() => scrollToSection('waarom')} className="block w-full text-left py-2 text-gray-600">Waarom</button>
            <button onClick={() => scrollToSection('features')} className="block w-full text-left py-2 text-gray-600">Voordelen</button>
            <button onClick={() => scrollToSection('pricing')} className="block w-full text-left py-2 text-gray-600">Prijzen</button>
            <button onClick={() => scrollToSection('demo')} className="block w-full text-left py-2 text-gray-600">Demo</button>
            <Link to="/over-ons" className="block w-full text-left py-2 text-gray-600" onClick={() => setMobileMenuOpen(false)}>Over Ons</Link>
            <Button 
              onClick={() => scrollToSection('pricing')} 
              className="w-full border-2 border-[#C9A227] bg-transparent text-[#C9A227] hover:bg-[#C9A227] hover:text-white font-semibold"
            >
              Start Nu
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-pattern pt-32 pb-20 md:pb-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Badge className="badge-accent px-4 py-1.5 text-sm font-medium" data-testid="hero-badge">
                  Next Level Protocol
                </Badge>
                <span className="text-sm font-medium text-[#C9A227]">Gebouwd door vakmensen, voor vakmensen</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight mb-6" data-testid="hero-title">
                Premium zekerheid voor elk vloerproject
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-xl">
                De digitale oplossing die vloerbedrijven helpt bij het automatiseren van klantcommunicatie. 
                Meer rust, duidelijkheid en een snellere doorloop van projecten.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => scrollToSection('pricing')} 
                  className="border-2 border-[#C9A227] bg-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] px-8 py-6 text-lg font-semibold"
                  data-testid="hero-cta-start"
                >
                  Start Nu <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => scrollToSection('demo')}
                  className="border-2 border-gray-300 text-gray-700 hover:border-[#C9A227] hover:text-[#C9A227] px-8 py-6 text-lg"
                  data-testid="hero-cta-demo"
                >
                  <Play className="mr-2 w-5 h-5" /> Bekijk Demo
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-12 pt-8 border-t border-[#C9A227]/20">
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">5+</div>
                  <div className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Uur/maand</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">€15k+</div>
                  <div className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Besparing/jaar</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">100%</div>
                  <div className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Duidelijkheid</div>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-[#C9A227]/30">
                <img 
                  src="https://images.pexels.com/photos/6474459/pexels-photo-6474459.jpeg" 
                  alt="Professional met tablet op bouwplaats"
                  className="w-full h-[500px] object-cover image-hover"
                />
                <div className="absolute bottom-6 left-6 right-6 glass rounded-xl p-4 float-animation border border-[#C9A227]/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#C9A227]/10 border border-[#C9A227] rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-[#C9A227]" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Protocol Verzonden</div>
                      <div className="text-sm text-gray-500">Vloerverwarming instructies gedeeld</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Veelgestelde Vragen */}
      <section id="waarom" className="py-20 md:py-32 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#C9A227]/20 text-[#C9A227] border border-[#C9A227] mb-6">Het Probleem</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              Kent u deze vragen?
            </h2>
            <p className="text-lg text-gray-400">
              Na elke vloerinstallatie krijgt u dezelfde vragen van klanten. Dit kost tijd, 
              leidt tot miscommunicatie en kan dure schadegevallen veroorzaken.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { 
                icon: Timer,
                question: "Wanneer mag ik de vloer weer belopen?", 
                impact: "Elke klant vraagt dit - gemiddeld 3x per project",
                color: "text-blue-400"
              },
              { 
                icon: Building2,
                question: "Wanneer mogen de meubels erop?", 
                impact: "Verkeerde informatie leidt tot beschadigingen",
                color: "text-green-400"
              },
              { 
                icon: ThermometerSun,
                question: "Wanneer kan de vloerverwarming aan?", 
                impact: "Te vroeg = scheuren, claims tot €8.000",
                color: "text-[#C9A227]"
              }
            ].map((item, index) => (
              <div key={index} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 hover:border-[#C9A227]/50 transition-colors">
                <item.icon className={`w-8 h-8 ${item.color} mb-4`} />
                <div className="text-xl font-semibold text-white mb-3">"{item.question}"</div>
                <div className="text-gray-400 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  {item.impact}
                </div>
              </div>
            ))}
          </div>

          {/* Cost of Problems */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Wat kost dit uw bedrijf?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-base sm:text-lg text-white mb-1">5+ uur per maand</div>
                    <div className="text-gray-400 text-sm sm:text-base">herhaalde uitleg via telefoon/e-mail</div>
                    <div className="text-[#C9A227] font-semibold mt-2 text-sm sm:text-base">= €4.000/jaar tijdverlies</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-base sm:text-lg text-white mb-1">€2.000 - €8.000 per schadegeval</div>
                    <div className="text-gray-400 text-sm sm:text-base">door verkeerd gebruik vloer</div>
                    <div className="text-[#C9A227] font-semibold mt-2 text-sm sm:text-base">= €10k - €15k/jaar faalkosten</div>
                  </div>
                </div>
              </div>
              <div className="bg-[#C9A227]/10 border border-[#C9A227] rounded-xl p-6">
                <div className="text-[#C9A227] font-semibold mb-2">Totale Impact</div>
                <div className="text-4xl font-bold text-white mb-2">€15.000 - €25.000</div>
                <div className="text-gray-400 mb-4">per jaar aan verloren tijd en vermijdbare kosten</div>
                <div className="text-sm text-gray-500">
                  Dit zijn conservatieve schattingen. Bij grotere bedrijven kan dit nog hoger oplopen.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="badge-accent mb-6">De Oplossing</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-6">
                Automatiseer uw klantcommunicatie
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                De Next Level Protocol App beantwoordt automatisch de meestgestelde vragen van uw klanten. 
                Geen herhaalde telefoontjes meer, geen misverstanden, geen onnodige schadegevallen.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "Automatische tijdslijnen voor elke vloersoort",
                  "Duidelijke protocollen voor vloerverwarming",
                  "Digitale verklaring met handtekening van klant",
                  "Professionele uitstraling richting klanten",
                  "Direct inzetbaar - geen ontwikkeltijd"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 border-2 border-[#C9A227] rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#C9A227]" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => scrollToSection('pricing')}
                className="border-2 border-[#C9A227] bg-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] px-8 py-6 text-lg font-semibold"
              >
                Bekijk Pakketten <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5606879/pexels-photo-5606879.jpeg" 
                alt="Detail werk aan houten vloer"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl border-2 border-[#C9A227]/30"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border-2 border-[#C9A227] rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#C9A227]/10 border border-[#C9A227] rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#C9A227]" />
                  </div>
                  <div className="font-semibold text-gray-900">Direct actief</div>
                </div>
                <p className="text-sm text-gray-600">
                  Geen maanden wachten op ontwikkeling. Start vandaag nog met de app.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="badge-accent mb-6">Voordelen</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-6">
              Waarom vloerbedrijven kiezen voor Next Level Protocol
            </h2>
            <p className="text-lg text-gray-600">
              Een bewezen oplossing die direct resultaat oplevert, zonder de complexiteit van maatwerk ontwikkeling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: "Tijdsbesparing",
                description: "Bespaar gemiddeld 5 uur per maand aan herhaalde uitleg. Uw team kan zich focussen op kernactiviteiten.",
                stat: "€4.000/jaar",
                statLabel: "aan tijdwaarde"
              },
              {
                icon: Shield,
                title: "Minder Faalkosten",
                description: "Voorkom dure schadegevallen door duidelijke protocollen. Eén voorkomen schadegeval betaalt de app terug.",
                stat: "€10k-€15k/jaar",
                statLabel: "besparing"
              },
              {
                icon: HeartHandshake,
                title: "Klanttevredenheid",
                description: "Klanten krijgen duidelijke, consistente informatie. Minder klachten, betere reviews.",
                stat: "100%",
                statLabel: "duidelijkheid"
              },
              {
                icon: Award,
                title: "Professionaliteit",
                description: "Positioneer uw bedrijf als moderne, georganiseerde dienstverlener met digitale protocollen.",
                stat: "White-label",
                statLabel: "optie beschikbaar"
              },
              {
                icon: FileCheck,
                title: "Juridische Zekerheid",
                description: "Digitale vastlegging met handtekening van de klant. Verminder aansprakelijkheidsrisico's.",
                stat: "Digitaal",
                statLabel: "ondertekend"
              },
              {
                icon: TrendingUp,
                title: "Snellere Doorlooptijd",
                description: "Soepeler projectverloop door het wegnemen van onzekerheden bij de klant.",
                stat: "Sneller",
                statLabel: "klaar"
              }
            ].map((feature, index) => (
              <Card key={index} className="feature-card border-2 border-gray-200 hover:border-[#C9A227] bg-white" data-testid={`feature-${index}`}>
                <CardHeader>
                  <div className="w-12 h-12 bg-[#C9A227]/10 border border-[#C9A227] rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#C9A227]" />
                  </div>
                  <CardTitle className="text-xl text-gray-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <div className="bg-[#C9A227]/5 border border-[#C9A227]/30 rounded-lg p-4">
                    <div className="text-2xl font-bold text-[#C9A227]">{feature.stat}</div>
                    <div className="text-sm text-gray-500">{feature.statLabel}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Not Build Your Own Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900 border-2 border-[#C9A227] rounded-3xl p-8 md:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-[#C9A227]/20 text-[#C9A227] border border-[#C9A227] mb-6">
                  Waarom geen eigen app?
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
                  Het zelf laten bouwen van een app kost een vermogen
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Een eigen app laten ontwikkelen is complex, duur en tijdrovend. 
                  Met Next Level Protocol krijgt u direct een bewezen oplossing.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="text-gray-400">UX/UI Ontwerp</span>
                    <span className="font-semibold text-white">€10.000 - €20.000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="text-gray-400">App Development</span>
                    <span className="font-semibold text-white">€40.000 - €80.000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="text-gray-400">Backend, Security & Opslag</span>
                    <span className="font-semibold text-white">€20.000 - €40.000</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="font-semibold text-lg text-white">Totale Investering</span>
                    <span className="font-bold text-xl text-red-400">€70.000 - €140.000</span>
                  </div>
                </div>

                <p className="text-gray-500 text-sm">
                  * Exclusief jaarlijkse onderhoudskosten en updates. Ontwikkeltijd: 6-12 maanden.
                </p>
              </div>

              <div className="bg-white border-2 border-[#C9A227] rounded-2xl p-8">
                <div className="text-center mb-8">
                  <div className="text-[#C9A227] font-semibold mb-2">Next Level Protocol</div>
                  <div className="text-5xl font-bold text-gray-900 mb-2">Vanaf €129</div>
                  <div className="text-gray-500">per maand</div>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    "Direct inzetbaar - geen wachttijd",
                    "Bewezen oplossing voor vloerbedrijven",
                    "Inclusief updates en onderhoud",
                    "White-label optie beschikbaar",
                    "Support en begeleiding"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#C9A227]" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 text-center">
                  <div className="text-green-600 font-semibold">U bespaart</div>
                  <div className="text-2xl font-bold text-gray-900">€68.000 - €138.000</div>
                  <div className="text-green-600 text-sm">vergeleken met eigen ontwikkeling</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="badge-accent mb-6">ROI Berekening</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-6">
              De app verdient zichzelf ruimschoots terug
            </h2>
          </div>

          <div className="bg-white border-2 border-[#C9A227]/30 rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-red-50 border-2 border-red-200 rounded-xl">
                <Calculator className="w-8 h-8 text-red-500 mx-auto mb-4" />
                <div className="text-sm text-gray-600 mb-2">Huidige kosten (zonder app)</div>
                <div className="text-3xl font-bold text-red-600">€15.000 - €25.000</div>
                <div className="text-sm text-gray-500 mt-2">per jaar aan tijdverlies en faalkosten</div>
              </div>

              <div className="text-center p-6 bg-[#C9A227]/5 border-2 border-[#C9A227] rounded-xl">
                <Euro className="w-8 h-8 text-[#C9A227] mx-auto mb-4" />
                <div className="text-sm text-gray-600 mb-2">Professional abonnement</div>
                <div className="text-3xl font-bold text-[#C9A227]">€2.148</div>
                <div className="text-sm text-gray-500 mt-2">per jaar (€179/maand)</div>
              </div>

              <div className="text-center p-6 bg-green-50 border-2 border-green-500 rounded-xl">
                <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-4" />
                <div className="text-sm text-gray-600 mb-2">Uw netto besparing</div>
                <div className="text-3xl font-bold text-green-600">€12.850 - €22.850</div>
                <div className="text-sm text-gray-500 mt-2">per jaar - ROI van 600% tot 1000%</div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-[#C9A227]/20 text-center">
              <p className="text-gray-600 mb-4">
                Met andere woorden: <span className="font-semibold text-gray-900">de app betaalt zichzelf 6 tot 10 keer terug</span> in het eerste jaar alleen al.
              </p>
              <Button 
                onClick={() => scrollToSection('pricing')}
                className="border-2 border-[#C9A227] bg-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] px-8 py-6 text-lg font-semibold"
              >
                Bekijk Pakketten <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="badge-accent mb-6">Prijzen</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-6">
              Kies uw pakket
            </h2>
            <p className="text-lg text-gray-600">
              Facturatie per maand na oplevering. Geen verborgen kosten. Opzegbaar per maand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {packages.map((pkg) => (
              <Card 
                key={pkg.id}
                className={`pricing-card relative bg-white ${
                  pkg.popular 
                    ? 'border-2 border-[#C9A227] ring-2 ring-[#C9A227] shadow-2xl shadow-[#C9A227]/20 scale-105 z-10' 
                    : 'border-2 border-gray-200'
                }`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#C9A227] text-white px-4 py-1 font-semibold border-0">Meest Gekozen</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl font-semibold text-gray-900">{pkg.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">€{pkg.price}</span>
                    <span className="text-gray-500 ml-1">{pkg.period}</span>
                  </div>
                  {pkg.setup_fee && (
                    <div className="text-sm text-gray-500 mt-2">
                      + €{pkg.setup_fee} eenmalige setup
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-4">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="pt-4">
                  <Button 
                    onClick={() => handleSelectPackage(pkg)}
                    className={`w-full py-6 text-base font-semibold ${
                      pkg.popular 
                        ? 'bg-[#C9A227] border-2 border-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A]' 
                        : 'bg-transparent border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white'
                    }`}
                    data-testid={`select-package-${pkg.id}`}
                  >
                    Selecteer {pkg.name}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Extra modules of maatwerk vragenlijsten nodig? 
              <span className="font-semibold text-gray-900"> Vanaf €395 per uitbreiding.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-2 border-[#C9A227]/30 rounded-2xl overflow-hidden shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <Badge className="badge-accent mb-6 w-fit">Demo Omgeving</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-6">
                  Ervaar de app zelf
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Bekijk onze demo omgeving en ontdek hoe de app werkt vanuit het perspectief van uw klant. 
                  Geen account nodig, direct toegang met de toegangscode.
                </p>
                <div className="bg-gray-50 border-2 border-[#C9A227]/30 rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">Demo URL</span>
                    <a 
                      href="https://demovloer-app.preview.emergentagent.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#C9A227] hover:text-[#B8922A] font-medium"
                    >
                      Openen
                    </a>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Toegangscode</span>
                    <span className="font-mono font-bold text-gray-900 bg-[#C9A227]/10 border border-[#C9A227] px-3 py-1 rounded">2222</span>
                  </div>
                </div>
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-8">
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">Let op:</span> De demo omgeving is 24 uur per sessie beschikbaar. 
                    Daarna schakelt deze automatisch uit. Start een nieuwe sessie om opnieuw toegang te krijgen.
                  </p>
                </div>
                <Button 
                  asChild
                  className="bg-[#C9A227] border-2 border-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] w-fit px-8 py-6 text-lg font-semibold"
                  data-testid="demo-btn"
                >
                  <a href="https://demovloer-app.preview.emergentagent.com" target="_blank" rel="noopener noreferrer">
                    <Smartphone className="mr-2 w-5 h-5" /> Open Demo Omgeving
                  </a>
                </Button>
              </div>
              <div className="relative h-64 lg:h-auto bg-gray-100">
                <img 
                  src="https://images.pexels.com/photos/3616756/pexels-photo-3616756.jpeg" 
                  alt="Professionele vloerinstallatie"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Klaar om te starten?
          </h2>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            Sluit vandaag nog aan en ervaar hoe de Next Level Protocol App uw werkprocessen transformeert. 
            Facturatie start pas na oplevering.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => scrollToSection('pricing')}
              className="bg-[#C9A227] border-2 border-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] px-8 py-6 text-lg font-semibold"
              data-testid="cta-start-btn"
            >
              Kies Uw Pakket <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              className="border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white px-8 py-6 text-lg"
              asChild
            >
              <a href="mailto:nextlevelgietvloeren@gmail.com">Contact Opnemen</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 border-t border-[#C9A227]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 border-2 border-[#C9A227] rounded-lg flex items-center justify-center">
                <span className="text-[#C9A227] font-bold text-sm">NL</span>
              </div>
              <span className="font-semibold text-white">Next Level Protocol</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/over-ons" className="hover:text-[#C9A227] transition-colors">Over Ons</Link>
              <a href="mailto:nextlevelgietvloeren@gmail.com" className="hover:text-[#C9A227] transition-colors">
                nextlevelgietvloeren@gmail.com
              </a>
            </div>
            <div className="text-sm text-gray-500">
              © 2024 Next Level Protocol. Alle rechten voorbehouden.
            </div>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      <SignupModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        selectedPackage={selectedPackage}
      />
    </div>
  );
}
